import { PlusIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { DeliveryActionButton } from "../delivery/action-controls"
import { DisabledReason, HelpButtonGroup } from "@dehub/theme"
import { SelectionBar } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"

interface Props {
  selectedCount: number
  selectedRows: IDeliveryNotePlus[]
  onClear: () => void
  onCreateRoute: () => void
  onAddToRoute: () => void
  /** «Ачилт баталгаажуулах»: PUT /delivery_note/approve, then the approve page. */
  onApproveLoading: () => void
  approving: boolean
  creatingRoute: boolean
}

/**
 * Bulk bar while rows are selected (canvas Ord_DeliveryHub): «Маршрутад нэмэх» opens AddToRouteDialog,
 * «Шинэ маршрут үүсгэх» opens the route wizard. Both need DEL_ROUTE_PLAN (O09, O15: the old
 * «Хуваарилах» button opened the same add-to-route dialog under a misleading name).
 */
export function PendingDeliveriesActionBar({
  selectedCount,
  selectedRows,
  onCreateRoute,
  onAddToRoute,
  onApproveLoading,
  approving,
  creatingRoute,
}: Props) {
  const { t } = useTranslation("delivery")
  const { permissionState, stateOf } = useDeliveryActions()
  if (selectedCount === 0) return null

  // O97: BE delivery-note/approve.ts takes ASSIGNED notes of one driver; say why otherwise.
  const staffIds = new Set(selectedRows.map((r) => r.staffId ?? ""))
  const approveReason = selectedRows.some((r) => !stateOf("approveLoading", r).applies)
    ? t("manage.approve.onlyAssigned")
    : staffIds.size > 1 || staffIds.has("")
      ? t("manage.approve.oneDriver")
      : undefined

  return (
    <SelectionBar label={t("manage.pending.selected", { count: selectedCount })}>
      {/* State-changing bulk actions: attached «?» (R2) and a reason popover while unavailable (R4, @dehub/help). */}
      <DisabledReason helpKey="order.supplier-delivery-list.reason.approve-loading" reason={!permissionState("approveLoading").allowed ? "PERM" : approveReason ? "NOT_READY" : null} when={!permissionState("approveLoading").allowed || !!approveReason} position="bottom-start">
        <HelpButtonGroup helpKey="order.supplier-delivery-list.action.approve-loading">
          <DeliveryActionButton
            data-help-id="bulk-approve-loading"
            state={permissionState("approveLoading")}
            variant="default"
            disabledReason={approveReason}
            loading={approving}
            onClick={onApproveLoading}
          >
            {t("manage.approve.action")}
          </DeliveryActionButton>
        </HelpButtonGroup>
      </DisabledReason>
      <DisabledReason helpKey="order.supplier-delivery-list.reason.add-to-route" reason="PERM" when={!permissionState("addToRoute").allowed} position="bottom-start">
        <HelpButtonGroup helpKey="order.supplier-delivery-list.action.add-to-route">
          <DeliveryActionButton state={permissionState("addToRoute")} variant="default" onClick={onAddToRoute}>
            {t("manage.action.addToRoute")}
          </DeliveryActionButton>
        </HelpButtonGroup>
      </DisabledReason>
      <DisabledReason helpKey="order.supplier-delivery-list.reason.create-route" reason="PERM" when={!permissionState("createRoute").allowed} position="bottom-start">
        <HelpButtonGroup helpKey="order.supplier-delivery-list.action.create-route">
          <DeliveryActionButton
            data-help-id="bulk-create-route"
            state={permissionState("createRoute")}
            leftSection={<PlusIcon size={16} />}
            loading={creatingRoute}
            onClick={onCreateRoute}
          >
            {t("manage.action.createRoute")}
          </DeliveryActionButton>
        </HelpButtonGroup>
      </DisabledReason>
    </SelectionBar>
  )
}
